import React from 'react';
import { useAuth } from '../hooks/useApi';

const pageStyle: React.CSSProperties = {
  maxWidth: 960,
  margin: '32px auto',
  padding: '0 16px 24px',
  fontFamily: 'system-ui, -apple-system, Segoe UI, Roboto, sans-serif',
  lineHeight: 1.65
};

const leadStyle: React.CSSProperties = {
  color: '#4b5563',
  marginBottom: 16
};

const sectionCardStyle: React.CSSProperties = {
  border: '1px solid #e5e7eb',
  borderRadius: 12,
  padding: '14px 16px',
  background: '#fff',
  boxShadow: '0 4px 18px rgba(0,0,0,0.05)',
  marginBottom: 14
};

const listStyle: React.CSSProperties = {
  color: '#374151',
  paddingLeft: 20,
  margin: '8px 0 0'
};

const AboutPage: React.FC = () => {
  const { user, loading } = useAuth();
  const showAuthCta = !loading && !user;

  return (
    <div style={pageStyle}>
      <h1 style={{ marginBottom: 10 }}>About LoConvo</h1>
      <p style={leadStyle}>
        LoConvo is a map-first conversation app. You can discuss real places in public, or build private/shared personal maps for trips, ideas, and memories.
      </p>

      <div style={sectionCardStyle}>
        <h3 style={{ margin: 0 }}>What makes LoConvo different</h3>
        <ul style={listStyle}>
          <li><strong>Conversations anchored to places:</strong> Topics can be tied to points, lines, or areas on the map.</li>
          <li><strong>Flexible visibility:</strong> Share topics and lists as public, only me, followers, mutuals, or specific people.</li>
          <li><strong>Privacy by choice:</strong> Post topics or comments anonymously when a place is sensitive, and opt your content out of search engine indexing entirely from your profile settings.</li>
          <li><strong>Two search modes:</strong> Find addresses on the map, and separately search users/topics/comments with optional radius filtering.</li>
          <li><strong>Personal map workflows:</strong> Save posts into lists, keep them private, or share selected collections on your profile.</li>
          <li><strong>Community controls:</strong> Follow users, message directly, report abuse, and mark sensitive content as NSFW.</li>
          <li><strong>Real-time notifications:</strong> Know within seconds when someone comments on your topic, replies to you, or when people you follow start a new discussion.</li>
        </ul>
      </div>

      <div style={sectionCardStyle}>
        <h3 style={{ margin: 0 }}>How people use it</h3>
        <ul style={listStyle}>
          <li>Discuss local improvements (streets, parks, transit, services) with map context.</li>
          <li>Create trip or neighborhood lists and share them with selected people.</li>
          <li>Collect local recommendations and attach notes to places that matter.</li>
          <li>Follow contributors whose local posts you trust and keep up through messages.</li>
        </ul>
      </div>

      <div style={sectionCardStyle}>
        <h3 style={{ margin: 0 }}>Safety and responsibility</h3>
        <p style={{ color: '#374151', marginTop: 8 }}>
          Location context is powerful and sensitive. Avoid posting precise private addresses without consent, use reports for abusive content, and do not use LoConvo for emergencies. When a place matters but your identity shouldn’t, post anonymously — and if you’d rather keep your contributions out of Google entirely, turn off search engine visibility in your profile settings. Read our <a href="/terms">Terms</a> and <a href="/privacy">Privacy Notice</a> for full rules and data handling details.
        </p>
      </div>

      {showAuthCta && (
        <>
          <h3 style={{ marginTop: 20, marginBottom: 8 }}>Get started</h3>
          <p style={{ color: '#374151', marginTop: 0 }}>
            Sign up with Google, Microsoft, or email. Then set your username and open the <a href="/how-to">How To guide</a> for step-by-step workflows.
          </p>
          <div style={{ marginTop: 24, display: 'flex', gap: 12 }}>
            <a href="/register" style={{ padding: '10px 14px', borderRadius: 8, background: '#2563eb', color: 'white', fontWeight: 700, textDecoration: 'none' }}>Create account</a>
            <a href="/login" style={{ padding: '10px 14px', borderRadius: 8, border: '1px solid #2563eb', color: '#2563eb', fontWeight: 700, textDecoration: 'none' }}>Log in</a>
          </div>
        </>
      )}

      {!showAuthCta && (
        <p style={{ color: '#4b5563', marginTop: 16 }}>
          Ready to explore more? Visit the <a href="/how-to">How To guide</a> for practical workflows and tips.
        </p>
      )}

      <p style={{ color: '#4b5563', marginTop: 16 }}>
        Questions: <a href="mailto:support@loconvo.com" style={{ color: '#2563eb' }}>support@loconvo.com</a>
      </p>
    </div>
  );
};

export default AboutPage;
